// toolbar.js - Enhanced minimalistic toolbar for pipeline editor
import React from "react";
import { DraggableNode } from "./draggableNode";
import { SubmitButton } from "./submit";
import { Layers, Zap, Component } from "lucide-react";
import Logo from "./components/Logo";

/**
 * Enhanced node configuration with categorization for better organization
 */
const NODE_CATEGORIES = {
  core: {
    label: "Core",
    icon: Layers,
    nodes: [
      { type: "customInput", label: "Input", color: "blue" },
      { type: "customOutput", label: "Output", color: "green" },
      { type: "text", label: "Text", color: "purple" },
      { type: "llm", label: "LLM", color: "emerald" },
    ],
  },
  processing: {
    label: "Processing",
    icon: Zap,
    nodes: [
      { type: "filter", label: "Filter", color: "orange" },
      { type: "transform", label: "Transform", color: "cyan" },
      { type: "aggregate", label: "Aggregate", color: "pink" },
      { type: "conditional", label: "Conditional", color: "indigo" },
      { type: "validator", label: "Validator", color: "orange" },
    ],
  },
  utilities: {
    label: "Advance",
    icon: Component,
    nodes: [
      { type: "api", label: "API Call", color: "indigo" },
      { type: "storage", label: "Storage", color: "slate" },
      { type: "join", label: "Join", color: "cyan" },
      { type: "split", label: "Split", color: "pink" },
      { type: "delay", label: "Delay", color: "yellow" },
    ],
  },
};

/**
 * PipelineToolbar component - Enhanced minimalistic floating toolbar
 * Shows all nodes always visible with clear category grouping
 */
export const PipelineToolbar = () => {
  const [activeCategory, setActiveCategory] = React.useState("core");

  // const getNodeClasses = (color) => {
  //   const colorMap = {
  //     blue: "border-blue-200 bg-blue-50/80 text-blue-700 hover:bg-blue-100 hover:border-blue-300",
  //     green:
  //       "border-green-200 bg-green-50/80 text-green-700 hover:bg-green-100 hover:border-green-300",
  //     purple:
  //       "border-purple-200 bg-purple-50/80 text-purple-700 hover:bg-purple-100 hover:border-purple-300",
  //     emerald:
  //       "border-emerald-200 bg-emerald-50/80 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300",
  //     orange:
  //       "border-orange-200 bg-orange-50/80 text-orange-700 hover:bg-orange-100 hover:border-orange-300",
  //     cyan: "border-cyan-200 bg-cyan-50/80 text-cyan-700 hover:bg-cyan-100 hover:border-cyan-300",
  //     pink: "border-pink-200 bg-pink-50/80 text-pink-700 hover:bg-pink-100 hover:border-pink-300",
  //     indigo:
  //       "border-indigo-200 bg-indigo-50/80 text-indigo-700 hover:bg-indigo-100 hover:border-indigo-300",
  //     slate:
  //       "border-slate-200 bg-slate-50/80 text-slate-700 hover:bg-slate-100 hover:border-slate-300",
  //     yellow:
  //       "border-yellow-200 bg-yellow-50/80 text-yellow-700 hover:bg-yellow-100 hover:border-yellow-300",
  //   };
  //   return colorMap[color] || colorMap.slate;
  // };

  const activeNodes = NODE_CATEGORIES[activeCategory]?.nodes || [];

  return (
    <div className="absolute top-0 w-full z-50  max-w-full bg-background border-b border-border backdrop-blur-lg p-3 px-4 shadow-sm">
      <div className="flex items-center justify-between">
        {/* Left: Toolbar */}
        <div className="space-y-4">
          {/* Row 1: Header with branding, category tabs, and submit */}
          <div className="flex items-center gap-4">
            {/* Left: Branding */}
            <div className="flex items-center gap-1.5">
              <Logo />
              <h1 className="text-xl font-semibold text-gray-900 tracking-tight">
                Editor
              </h1>
            </div>

            {/* Center: Category tabs with icons */}
            <div className="flex items-center ">
              {Object.entries(NODE_CATEGORIES).map(([key, category]) => {
                const isActive = activeCategory === key;
                const IconComponent = category.icon;

                return (
                  <button
                    key={key}
                    onClick={() => setActiveCategory(key)}
                    className={`flex items-center gap-2 px-3 py-1 text-sm font-medium  transition-all duration-200 ${isActive
                      ? "bg-purple-500 text-white shadow-sm"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-800"
                      }`}
                  >
                    <IconComponent size={16} />
                    <span>{category.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Row 2: Active category nodes */}
          <div className="flex items-center gap-2">
            {activeNodes.map((node) => (
              <DraggableNode
                key={node.type}
                type={node.type}
                label={node.label}
                className={`px-2 py-1 uppercase rounded-sm transition-all duration-200 text-xs font-medium tracking-tight shadow-sm hover:shadow-md transform hover:scale-105 bg-primary/40 hover:bg-primary/70 border border-primary`}
              />
            ))}
          </div>
        </div>

        {/* Right: Submit button */}
        <div className="flex items-center">
          <SubmitButton />
        </div>
      </div>
    </div>
  );
};
